/**
 * Acesso ao sistema segundo o estado da assinatura.
 *
 * Codigo puro, sem I/O. A pergunta que este arquivo responde e uma so: hoje,
 * esta empresa pode usar o Samev?
 *
 * O vies e deliberado: na duvida, LIBERA e avisa. Cortar o atendimento de um
 * cliente no meio do expediente porque um webhook do Asaas atrasou e um
 * jeito rapido de perder o cliente; deixar rodar um dia a mais de graca nao e.
 *
 * Referencia: PLANO.md secao 6.10.
 */

export type StatusAssinatura =
  /** Nunca assinou. Vale o periodo de teste, se ainda houver. */
  | "sem_assinatura"
  | "ativa"
  /** Cobranca vencida e nao paga no Asaas. */
  | "inadimplente"
  | "cancelada";

export interface Assinatura {
  status: StatusAssinatura;
  /** Fim do ciclo pago atual. */
  periodoFim: Date | null;
  /** Fim do periodo de teste. */
  trialFim: Date | null;
  /** Cancelou, mas segue valendo ate o fim do ciclo. */
  cancelarNoFim: boolean;
}

export type Acesso =
  | { nivel: "liberado" }
  | { nivel: "aviso"; motivo: string; diasRestantes: number }
  | { nivel: "bloqueado"; motivo: string };

export interface PoliticaBilling {
  /** Dias que o cliente continua rodando depois de o pagamento falhar. */
  carenciaDias: number;
  /** Comeca a avisar quando faltar este tanto de dias. */
  avisarDiasAntes: number;
}

export const POLITICA_PADRAO: PoliticaBilling = { carenciaDias: 7, avisarDiasAntes: 5 };

const DIA = 86_400_000;

function diasAte(quando: Date, agora: Date): number {
  return Math.ceil((quando.getTime() - agora.getTime()) / DIA);
}

export function avaliarAcesso(params: {
  assinatura: Assinatura;
  agora: Date;
  politica?: PoliticaBilling;
}): Acesso {
  const { assinatura: a, agora } = params;
  const politica = params.politica ?? POLITICA_PADRAO;

  if (a.status === "ativa") {
    // Ciclo vencido com status ativo quer dizer webhook atrasado, nao calote.
    // Vale a carencia antes de bloquear: a fonte de verdade e o Asaas, e ele
    // ainda nao disse que parou.
    if (a.periodoFim && a.periodoFim <= agora) {
      const limite = new Date(a.periodoFim.getTime() + politica.carenciaDias * DIA);
      if (agora >= limite) {
        return { nivel: "bloqueado", motivo: "A assinatura venceu e não foi renovada." };
      }
      return {
        nivel: "aviso",
        motivo: "Estamos confirmando a renovação com a operadora.",
        diasRestantes: diasAte(limite, agora),
      };
    }

    if (a.cancelarNoFim && a.periodoFim) {
      return {
        nivel: "aviso",
        motivo: "A assinatura foi cancelada e vale até o fim deste ciclo.",
        diasRestantes: diasAte(a.periodoFim, agora),
      };
    }

    return { nivel: "liberado" };
  }

  if (a.status === "inadimplente") {
    // A carencia conta do fim do ciclo, nao da primeira tentativa: e o momento
    // em que o cliente deixou de ter periodo pago.
    const base = a.periodoFim ?? agora;
    const limite = new Date(base.getTime() + politica.carenciaDias * DIA);

    if (agora >= limite) {
      return { nivel: "bloqueado", motivo: "O pagamento não foi confirmado." };
    }
    return {
      nivel: "aviso",
      motivo: "A fatura venceu e não foi paga. Pague para não perder o acesso.",
      diasRestantes: diasAte(limite, agora),
    };
  }

  if (a.status === "cancelada") {
    // Cancelou mas ainda tem ciclo pago: ele pagou por esses dias.
    if (a.periodoFim && a.periodoFim > agora) {
      return {
        nivel: "aviso",
        motivo: "A assinatura foi cancelada e vale até o fim deste ciclo.",
        diasRestantes: diasAte(a.periodoFim, agora),
      };
    }
    return { nivel: "bloqueado", motivo: "A assinatura foi cancelada." };
  }

  // Nunca assinou: sobra o periodo de teste.
  if (a.trialFim && a.trialFim > agora) {
    const restam = diasAte(a.trialFim, agora);
    if (restam <= politica.avisarDiasAntes) {
      return {
        nivel: "aviso",
        motivo: "Seu período de teste está acabando.",
        diasRestantes: restam,
      };
    }
    return { nivel: "liberado" };
  }

  return { nivel: "bloqueado", motivo: "O período de teste terminou." };
}

/**
 * Assentos que a assinatura precisa cobrir.
 *
 * Sempre pelo menos um: uma empresa sem membro ativo e um estado transitorio de
 * convite, e cobrar zero assento nao faz sentido.
 */
export function assentosNecessarios(membrosAtivos: number): number {
  return Math.max(1, membrosAtivos);
}

/**
 * Assento so DESCE na virada do ciclo.
 *
 * Se descesse na hora, quem remove e reconvida um membro no mesmo mes geraria
 * credito proporcional a cada clique, e a fatura viraria uma lista de ajustes
 * que ninguem consegue conferir.
 */
export function assentosACobrar(params: {
  assentosAtuais: number;
  membrosAtivos: number;
  viradaDeCiclo: boolean;
}): number {
  const necessarios = assentosNecessarios(params.membrosAtivos);
  if (params.viradaDeCiclo) return necessarios;
  return Math.max(params.assentosAtuais, necessarios);
}
